import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { Link } from 'react-router-dom';
import { LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, CardConfirm, ListCard, ListCards } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import './TiposTarefaPage.css';
import { TipoTarefaService, type TipoTarefa } from '../services/TarefaService';
import { ModeloTarefaService, type ModeloTarefa } from '../services/ModeloTarefaService';

/**
 * Configurações › Vendas › Tipos de tarefa (docs/propostas/tarefas-padrao.html §8, fase 2).
 *
 * Cadastro de um campo só, no molde de Motivos de perda: ListKit na lista e
 * RecordPanel de dois modos. O que muda em relação ao molde é o aviso de uso —
 * um tipo usado pelo trilho não deve sumir sem que a pessoa saiba, porque o
 * modelo de tarefa aponta para ele.
 */
const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const TiposTarefaPage = () => {
    const celular = useCelular();
    const [tipos, setTipos] = useState<TipoTarefa[]>([]);
    const [trilho, setTrilho] = useState<ModeloTarefa[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Recorte de trabalho, na sessao da aba. */
    const [recorte, gravarRecorte] = useRecorte('tt:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<TipoTarefa | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [nome, setNome] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [ts, t] = await Promise.all([
                TipoTarefaService.getAll(),
                /* Só para dizer quem está em uso pelo trilho; a tela não depende disso. */
                ModeloTarefaService.listar().then(r => r.etapas).catch(() => [] as ModeloTarefa[]),
            ]);
            setTipos(ts); setTrilho(t);
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    /* Em que etapas do trilho o tipo é usado — o texto do aviso e do modo ver. */
    const usoNoTrilho = useCallback(
        (tipoTarefaId: number) => trilho.filter(e => e.ativo && e.tipoTarefaId === tipoTarefaId),
        [trilho]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t ? tipos.filter(x => (x.nome || '').toLowerCase().includes(t)) : tipos;
    }, [tipos, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const abrirVer = (t: TipoTarefa) => { setAberto(t); setModo('ver'); setErro(''); };
    const abrirEdicao = (t: TipoTarefa) => { setAberto(t); setNome(t.nome); setModo('editar'); setErro(''); };
    const abrirNovo = () => {
        setAberto({ tipoTarefaId: 0, nome: '', dataCriacao: '' });
        setNome(''); setModo('editar'); setErro('');
    };
    const fechar = () => { setAberto(null); setErro(''); };

    const ehNovo = modo === 'editar' && aberto?.tipoTarefaId === 0;

    const salvar = async () => {
        const texto = nome.trim();
        if (!texto) { setErro('Informe o nome do tipo.'); return; }

        /* Tipo repetido não quebra a API, mas duplica a opção na hora de criar
           a tarefa — e ninguém percebe que criou dois "Ligação". */
        const repetido = tipos.some(t =>
            t.tipoTarefaId !== aberto?.tipoTarefaId &&
            (t.nome || '').trim().toLowerCase() === texto.toLowerCase());
        if (repetido) { setErro('Já existe um tipo com esse nome.'); return; }

        setSalvando(true);
        try {
            const r = ehNovo
                ? await TipoTarefaService.criar(texto)
                : await TipoTarefaService.atualizar(aberto!.tipoTarefaId, texto);
            if (r.sucesso) {
                aviso.ok(ehNovo ? 'Tipo criado.' : 'Tipo atualizado.');
                await carregar(); fechar();
            } else aviso.erro(r.mensagem || 'Não foi possível salvar o tipo.');
        } catch {
            aviso.erro('Não foi possível salvar o tipo.');
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional pelas duas portas da exclusão: a lixeira da linha e o
       rodapé do painel. A pergunta mora na linha (`RowConfirm`) ou no Dialog. */
    const excluir = async (alvo = aberto) => {
        if (!alvo?.tipoTarefaId) return;
        setSalvando(true);
        try {
            const r = await TipoTarefaService.remover(alvo.tipoTarefaId);
            if (r.sucesso) { aviso.ok('Tipo excluído.'); await carregar(); fechar(); }
            else aviso.erro(r.mensagem || 'Não foi possível excluir o tipo.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
            setAConfirmar(null);
        }
    };

    const COLUNAS = '3fr 1.2fr 1fr 78px';

    const perguntaExcluir = (t: TipoTarefa) => {
        const uso = usoNoTrilho(t.tipoTarefaId);
        return uso.length > 0
            ? `Excluir "${t.nome}"? O trilho usa este tipo em ${uso.length} ${uso.length === 1 ? 'etapa' : 'etapas'}.`
            : `Excluir "${t.nome}"?`;
    };

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {tipos.length === 0 ? 'Nenhum tipo cadastrado' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {tipos.length === 0
                    ? 'Sem tipos cadastrados, toda tarefa nasce sem dizer como é feita — e o trilho não consegue pedir uma ligação numa etapa e uma reunião na outra.'
                    : 'Ajuste a busca para ver os outros.'}
            </p>
            {tipos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo tipo
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Tipos de tarefa"
                subtitle="Como cada tarefa é feita — ligação, reunião, e-mail. É o que o trilho escolhe para o próximo passo."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && tipos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo tipo
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar tipo…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {tipos.length} {tipos.length === 1 ? 'tipo' : 'tipos'}
                    </span>
                </div>

                {/* Celular: a grade de quatro colunas não cabe na largura do
                    telefone, então a linha vira cartão — o nome, quantas etapas
                    do trilho usam o tipo e quando ele entrou. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(t => {
                                    const uso = usoNoTrilho(t.tipoTarefaId);
                                    return aConfirmar === t.tipoTarefaId ? (
                                        <CardConfirm key={t.tipoTarefaId}
                                            question={perguntaExcluir(t)}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(t)} />
                                    ) : (
                                        <ListCard
                                            key={t.tipoTarefaId}
                                            titulo={t.nome}
                                            apoio={uso.length === 0
                                                ? 'fora do trilho'
                                                : `${uso.length} ${uso.length === 1 ? 'etapa' : 'etapas'} do trilho`}
                                            estado={`Criado em ${dataCurta(t.dataCriacao)}`}
                                            selected={aberto?.tipoTarefaId === t.tipoTarefaId}
                                            label={`Abrir ${t.nome}`}
                                            onOpen={() => abrirVer(t)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir tipo" danger
                                                    onClick={() => setAConfirmar(t.tipoTarefaId)} />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Tipo</div>
                            <div className="lk-head">No trilho</div>
                            <div className="lk-head">Criado em</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(t => {
                                    const uso = usoNoTrilho(t.tipoTarefaId);
                                    return aConfirmar === t.tipoTarefaId ? (
                                        <RowConfirm key={t.tipoTarefaId}
                                            question={perguntaExcluir(t)}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(t)} />
                                    ) : (
                                        <ListRow key={t.tipoTarefaId} columns={COLUNAS}
                                            selected={aberto?.tipoTarefaId === t.tipoTarefaId}>
                                            <span className="lk-cell lk-strong"><span>{t.nome}</span></span>
                                            <span className="lk-cell lk-muted">
                                                <span>{uso.length === 0 ? '—' : `${uso.length} ${uso.length === 1 ? 'etapa' : 'etapas'}`}</span>
                                            </span>
                                            <span className="lk-cell lk-muted"><span>{dataCurta(t.dataCriacao)}</span></span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver tipo"
                                                    onClick={() => abrirVer(t)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir tipo" danger
                                                    onClick={() => setAConfirmar(t.tipoTarefaId)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                title={modo === 'editar'
                    ? (nome.trim() || (ehNovo ? 'Novo tipo' : 'Tipo de tarefa'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto && !ehNovo ? `Criado em ${dataCurta(aberto.dataCriacao)}` : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && !ehNovo && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => excluir()} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && (
                    <PanelSection title="Tipo">
                        <p className="tt-texto">{aberto.nome}</p>
                        <p className="tt-uso">
                            {usoNoTrilho(aberto.tipoTarefaId).length === 0
                                ? 'Nenhuma etapa do trilho usa este tipo.'
                                : `Usado no trilho em: ${usoNoTrilho(aberto.tipoTarefaId).map(e => e.titulo).join(', ')}.`}
                            {' '}
                            <Link className="tt-link" to="/tarefas-padrao">Abrir Tarefas padrão</Link>
                        </p>
                    </PanelSection>
                )}

                {modo === 'editar' && (
                    <PanelSection title="Tipo">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tt-nome">Nome</label>
                            <input id="tt-nome" className="rp-input" type="text" autoFocus
                                value={nome} placeholder="Ex.: Ligação"
                                onChange={e => { setNome(e.target.value); setErro(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') salvar(); }} />
                            {erro
                                ? <span className="rp-error">{erro}</span>
                                : <span className="tt-hint">
                                    Aparece na lista de tipos ao criar uma tarefa e na configuração de cada etapa do trilho.
                                  </span>}
                        </div>
                    </PanelSection>
                )}

                {/* Histórico: seção no fim do modo ver (painel sem abas). */}
                {modo === 'ver' && aberto && (
                    <SecaoHistorico entidade="tipo_tarefa" id={aberto.tipoTarefaId} />
                )}
            </RecordPanel>
        </div>
    );
};

export default TiposTarefaPage;
